import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { NavigatingButton } from '@sorare/core/src/atoms/buttons/NavigatingButton';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_RANKINGS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';

import { RankRow } from '#src/components/leaderboard/CumulativeRankRewards/CumulativeRankRow/index.tsx';
import { CompactSo5Lineup } from '#src/components/lineup/CompactSo5Lineup/index.tsx';

import type {
  MyCircleLeaderboardQuery,
  MyCircleLeaderboardQueryVariables,
} from './__generated__/index.graphql.ts';

const MY_CIRCLE_LEADERBOARD_QUERY = gql`
  query MyCircleLeaderboardQuery($slug: String!, $cursor: String) {
    so5 {
      so5Leaderboard(slug: $slug) {
        slug
        so5Fixture {
          slug
          type
        }
        so5Rankings(first: 10, onlyFollowed: true, after: $cursor) {
          pageInfo {
            hasNextPage
            endCursor
          }
          nodes {
            id
            ranking
            score
            so5Lineup {
              id
              ...CompactSo5Lineup_so5Lineup
            }
            ...RankRow_userRankingInterface
          }
        }
      }
    }
  }
  ${CompactSo5Lineup.fragments.so5Lineup}
  ${RankRow.fragments.userRankingInterface}
` as TypedDocumentNode<
  MyCircleLeaderboardQuery,
  MyCircleLeaderboardQueryVariables
>;

type Props = {
  leaderboard: string;
};
export const MyCircleLeaderboard = ({ leaderboard }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { data, loadMore, loading } = usePaginatedQuery(
    MY_CIRCLE_LEADERBOARD_QUERY,
    {
      variables: {
        slug: leaderboard,
      },
      connections: ['So5RankingConnection'],
    }
  );

  const { so5Leaderboard } = data?.so5 || {};
  const { so5Rankings, so5Fixture } = so5Leaderboard || {};

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, { cursor: so5Rankings?.pageInfo.endCursor });
    },
    !!so5Rankings?.pageInfo.hasNextPage && !!so5Rankings?.pageInfo.endCursor,
    loading
  );

  return (
    <div>
      <Vertical>
        <BrandM>
          <FormattedMessage {...glossary.myCircle} />
        </BrandM>
        {so5Rankings?.nodes?.map(rank => (
          <RankRow key={rank.id} ranking={rank}>
            {() => {
              return <CompactSo5Lineup so5Lineup={rank.so5Lineup} />;
            }}
          </RankRow>
        ))}
        <InfiniteScrollLoader />
      </Vertical>
      <Portal id="contender-drawer-content-sticky-footer">
        <NavigatingButton
          color="tertiary"
          size="medium"
          to={generateSportContextPath(
            ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_RANKINGS,
            {
              params: {
                eventType: so5Fixture?.type.toLowerCase() || '',
                leaderboard,
              },
            }
          )}
        >
          <FormattedMessage {...fantasy.viewLeaderboard} />
        </NavigatingButton>
      </Portal>
    </div>
  );
};
